import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FeatureFlag} from 'react-launch-darkly';

import {
    daysUntilDate,
    numberWithCommas,
    prettyDollars,
    prettyNumber,
    percent,
    scaleNumber
} from 'js/common/string_helper';

// Components
import RequireBrandSession from 'js/require_brand_session';
import StoryAnalyticsCards from './story_analytics_cards';
import {AnalyticsCard} from 'js/common/campaign_analytics_helper';
import {getCampaignWorkflow} from 'js/common/workflows';

const {CampaignType} = sn.enums;

class AnalyticsContainer extends Component {
    constructor(props) {
        super(props);
        const {flightEnd} = props;
        const daysUntilFlightEnd = daysUntilDate(flightEnd);
        const daysLeft = daysUntilFlightEnd > 0 ? daysUntilFlightEnd : 0;

        this.state = {
            daysLeftInCampaign: daysLeft
        };
    }
    displayBidAndBudgetRow = () => {
        const {
            bid,
            budget,
            ecpx,
            objectivBidType,
            percentDelivered,
            showBidAndBudgetRow,
            isSalesUser,
            brandId
        } = this.props;

        if (showBidAndBudgetRow) {
            return (
                <>
                    <AnalyticsCard
                        isSalesUser={isSalesUser}
                        brandId={brandId}
                        label={`Bid (${objectivBidType.toUpperCase()})`}
                        data={prettyDollars(bid)}
                        icon={require('images/admin/campaign_analytics/icons/bid.png')}
                        helpText={
                            'The price you are bidding for content at the start of the campaign - based on cost per content created (CPCC) or per engagement (CPE)'
                        }
                        tooltipColor="light"
                        place="top"
                        multiline
                        inline
                    />
                    <AnalyticsCard
                        isSalesUser={isSalesUser}
                        brandId={brandId}
                        label={`e${objectivBidType.toUpperCase()}`}
                        data={prettyDollars(ecpx)}
                        icon={require('images/admin/campaign_analytics/icons/ecpe.png')}
                        helpText={
                            'The effective price you are paying for your primary metric (CPE or CPCC) as determined at the end of the campaign'
                        }
                        tooltipColor="light"
                        place="top"
                        multiline
                        inline
                    />
                    <AnalyticsCard
                        isSalesUser={isSalesUser}
                        brandId={brandId}
                        label="% Delivered"
                        data={percent(percentDelivered, 0)}
                        icon={require('images/admin/campaign_analytics/icons/graph.png')}
                        helpText={
                            "The percentage of delivery based off the campaign's primary metric (CPE or CPCC)"
                        }
                        tooltipColor="light"
                        place="top"
                        multiline
                        inline
                    />
                    <AnalyticsCard
                        isSalesUser={isSalesUser}
                        brandId={brandId}
                        label="Total Budget"
                        data={`$${numberWithCommas(Math.floor(budget))}`}
                        icon={require('images/admin/campaign_analytics/icons/budget.png')}
                        helpText="The total monetary budget for the entire campaign"
                        tooltipColor="light"
                        place="top"
                        multiline
                        inline
                    />
                </>
            );
        }
        return null;
    };

    getEngagementsHelpText = () => {
        const {campaignType} = this.props;

        switch (campaignType) {
            case CampaignType.INSTAGRAM:
                return 'Likes + Comments + Shares';
            case CampaignType.TWITTER:
                return 'Retweets + Favorites';
            case CampaignType.SNAPCHAT:
                return 'Video Views';
            case CampaignType.YOUTUBE:
                return 'Likes + Comments + Video Views';
            default:
                return 'Total of likes and comments across a campaign; these vary across platforms';
        }
    };

    displayStoryAnalyticsCards = () => {
        if (window.sn.isSharedCampaignView) {
            return null;
        }

        return (
            <RequireBrandSession>
                <StoryAnalyticsCards>
                    {storyCards => (
                        <div className="row">
                            {storyCards}
                            <AnalyticsCard
                                isSalesUser={this.props.isSalesUser}
                                brandId={this.props.brandId}
                                label="Days Left in Campaign"
                                data={this.state.daysLeftInCampaign}
                                icon={require('images/admin/campaign_analytics/icons/notification.png')}
                            />
                        </div>
                    )}
                </StoryAnalyticsCards>
            </RequireBrandSession>
        );
    };

    displayVideoViews = () => {
        const {totalViews, isSalesUser, brandId} = this.props;

        return (
            <AnalyticsCard
                isSalesUser={isSalesUser}
                brandId={brandId}
                label="Video Views"
                data={scaleNumber(totalViews)}
                icon={require('images/admin/campaign_analytics/icons/users.png')}
            />
        );
    };

    displayImpressions = (show = 'off') => {
        const {totalImpressions, isSalesUser, brandId, totalViews} = this.props;

        const impressions = totalImpressions > 0 ? totalImpressions : totalViews;

        return (
            <AnalyticsCard
                isSalesUser={isSalesUser}
                brandId={brandId}
                label="Impressions"
                data={scaleNumber(impressions)}
                helpText={'Number of times content was shown to users'}
                icon={require('images/admin/campaign_analytics/icons/users.png')}
            />
        );
    };

    render() {
        const {
            campaignType,
            countBrandVisiblePosts,
            countInternalVisiblePosts,
            loading,
            totalComments,
            totalEngagements,
            totalLikes,
            totalScreenshots,
            totalReach,
            totalPotentialExposure: _totalPotentialExposure,
            totalViewers,
            totalPlays,
            totalViews,
            totalShares,
            isSalesUser,
            contentWorkflow,
            brandId,
            isTikTok = false
        } = this.props;

        if (loading) {
            return null;
        }

        const bidAndBudgetRow = this.displayBidAndBudgetRow();
        const engagementsHelpText = this.getEngagementsHelpText();
        const engagements =
            totalEngagements < 1000 ? totalEngagements : prettyNumber(totalEngagements, 1);
        const workflow = getCampaignWorkflow(contentWorkflow);

        const reach = totalReach < 1000 ? totalReach : prettyNumber(totalReach, 1);
        let likes = totalLikes < 1000 ? totalLikes : prettyNumber(totalLikes, 1);
        if (campaignType === 'twitter') {
            likes = totalScreenshots < 1000 ? totalScreenshots : prettyNumber(totalScreenshots, 1);
        }
        const comments = totalComments < 1000 ? totalComments : prettyNumber(totalComments, 1);

        const countVisiblePosts = workflow.showInternalVisiblePosts
            ? numberWithCommas(countInternalVisiblePosts)
            : numberWithCommas(countBrandVisiblePosts);

        const totalPotentialExposure =
            _totalPotentialExposure < 1000
                ? _totalPotentialExposure
                : prettyNumber(_totalPotentialExposure, 1);

        if (loading) {
            return null;
        }

        return (
            <div className="summary-container">
                <AnalyticsCard
                    isSalesUser={isSalesUser}
                    brandId={brandId}
                    label="Engagements"
                    data={engagements}
                    icon={require('images/admin/campaign_analytics/icons/heart.png')}
                    helpText={engagementsHelpText}
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    isSalesUser={isSalesUser}
                    brandId={brandId}
                    label="Engagement Rate"
                    data={percent(this.props.engagementsPercentage)}
                    icon={require('images/admin/campaign_analytics/icons/general.png')}
                    helpText={
                        this.props.isTikTok
                            ? 'Average of (Likes+Comments+Shares)/Views x 100'
                            : 'Average of (Likes+Comments+Shares)/Impressions x 100'
                    }
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    label="Creators"
                    data={numberWithCommas(this.props.userCount)}
                    icon={require('images/admin/campaign_analytics/icons/creator.png')}
                    helpText={
                        'Total number of talented individuals in our network that we have identified to create content for your campaign and have submitted'
                    }
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    label="Content Created"
                    data={countVisiblePosts}
                    icon={require('images/admin/campaign_analytics/icons/screenshots.png')}
                    helpText={'Total pieces of content that have been submitted to this campaign'}
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    isSalesUser={isSalesUser}
                    brandId={brandId}
                    label="Reach"
                    data={reach}
                    icon={require('images/admin/campaign_analytics/icons/reach.png')}
                    helpText={'Total number of unique accounts that have seen the media object'}
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    brandId={brandId}
                    label="Total Potential Exposure"
                    data={totalPotentialExposure}
                    icon={require('images/admin/campaign_analytics/icons/total_potential_exposure.png')}
                    helpText={
                        'Sum of total followers across all participating creators and multiplied by their unique posts in the campaign'
                    }
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                {!isTikTok && campaignType === CampaignType.INSTAGRAM && !!totalPlays && (
                    <AnalyticsCard
                        brandId={brandId}
                        label="Plays"
                        data={numberWithCommas(totalPlays)}
                        icon={require('images/admin/campaign_analytics/icons/total_plays.png')}
                        helpText={'Total # of reels plays'}
                        tooltipColor="light"
                        place="top"
                        multiline
                        inline
                    />
                )}

                {isTikTok ||
                    (campaignType === CampaignType.INSTAGRAM && (
                        <AnalyticsCard
                            brandId={brandId}
                            label="Total Viewers"
                            data={numberWithCommas(totalViewers)}
                            icon={require('images/admin/campaign_analytics/icons/users.png')}
                            helpText={'Total # of unique views of all posts within campaign'}
                            tooltipColor="light"
                            place="top"
                            multiline
                            inline
                        />
                    ))}

                {isTikTok && (
                    <AnalyticsCard
                        brandId={brandId}
                        label="Views"
                        data={numberWithCommas(totalViews)}
                        icon={require('images/admin/campaign_analytics/icons/views.png')}
                        helpText={'Sum of all views of posts within campaign'}
                        tooltipColor="light"
                        place="top"
                        multiline
                        inline
                    />
                )}

                <FeatureFlag
                    flagKey="campaign-data-impressions-metric"
                    renderFeatureCallback={this.displayImpressions}
                    renderDefaultCallback={this.displayVideoViews}
                />

                <AnalyticsCard
                    isSalesUser={isSalesUser}
                    brandId={brandId}
                    label="Likes"
                    data={likes}
                    icon={require('images/admin/campaign_analytics/icons/like.png')}
                    helpText="Total likes across all pieces of content in the campaign"
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    isSalesUser={isSalesUser}
                    brandId={brandId}
                    label="Comments"
                    data={comments}
                    icon={require('images/admin/campaign_analytics/icons/comment.png')}
                    helpText="Total comments across all pieces of content in the campaign"
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                <AnalyticsCard
                    brandId={brandId}
                    label="Shares"
                    data={numberWithCommas(totalShares)}
                    icon={require('images/admin/campaign_analytics/icons/shares.png')}
                    helpText="Sum of all shares of posts within campaign"
                    tooltipColor="light"
                    place="top"
                    multiline
                    inline
                />

                {this.displayStoryAnalyticsCards()}

                {bidAndBudgetRow}
            </div>
        );
    }
}

AnalyticsContainer.propTypes = {
    bid: PropTypes.number.isRequired,
    budget: PropTypes.number.isRequired,
    countBrandVisiblePosts: PropTypes.number.isRequired,
    ecpx: PropTypes.number.isRequired,
    engagementsPercentage: PropTypes.number.isRequired,
    flightEnd: PropTypes.number.isRequired,
    koer: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
    objectivBidType: PropTypes.string.isRequired,
    percentDelivered: PropTypes.number.isRequired,
    showKoer: PropTypes.number.isRequired,
    showBidAndBudgetRow: PropTypes.bool.isRequired,
    totalComments: PropTypes.number.isRequired,
    totalEngagements: PropTypes.number.isRequired,
    totalLikes: PropTypes.number.isRequired,
    totalReach: PropTypes.number.isRequired,
    totalViews: PropTypes.number.isRequired,
    totalPotentialExposure: PropTypes.number.isRequired,
    totalViewers: PropTypes.number.isRequired,
    totalShares: PropTypes.number.isRequired,
    totalPlays: PropTypes.number.isRequired,
    userCount: PropTypes.number.isRequired,
    isSalesUser: PropTypes.bool.isRequired,
    brandId: PropTypes.number.isRequired,
    isTikTok: PropTypes.bool
};

export default AnalyticsContainer;



// WEBPACK FOOTER //
// ./campaign_analytics/v2/campaign_analytics_page/analytics/index.js